import { useSimMode } from "../SimModeContext";

/**
 * Subset of the apps/main `UseAccessControlResult` shape that widgets
 * actually destructure. Kept loose to avoid coupling shared to
 * apps/main types; the apps/main augmentation re-types the injected
 * real hook precisely via module augmentation.
 */
interface SimAccessControlResult {
    isAllowed: boolean;
    isChecked: boolean;
}

const SIM_ACCESS_ALLOWED: SimAccessControlResult = {
    isAllowed: true,
    isChecked: true
};

/**
 * Resolver hook for widget-side access control.
 *
 * - In sim mode (no `realHooks.useAccessControl` injected): returns
 *   `{ isAllowed: true, isChecked: true }` so the submit CTA isn't
 *   blocked by gating UI before the orchestrator's phase schedule
 *   fires.
 * - When `realHooks.useAccessControl` is present (apps/main wiring
 *   via `SimRealHooksProvider`): calls the injected real hook,
 *   preserving production gate behavior.
 *
 * Crucially, **this file has no static import of the apps/main
 * `useAccessControl`**, which would transitively pull
 * `@solomon-apps/shared`'s `useWallet` → the Solomon SDK chain.
 * External (sim-only) consumers can therefore bundle this orchestrator
 * without dragging the SDK in.
 */
export function useAccessControlOrchestration<TReturn = SimAccessControlResult>(): TReturn {
    const sim = useSimMode();
    const useReal = (sim.realHooks as Record<string, unknown> | undefined)?.["useAccessControl"] as
        | (() => TReturn)
        | undefined;
    const useFallback = (): TReturn => SIM_ACCESS_ALLOWED as unknown as TReturn;
    const useImplementation = useReal ?? useFallback;
    return useImplementation();
}
